/**
 * Theme helper — toggle dark mode con persistenza localStorage.
 *
 * Strategia Tailwind 'class': la classe `dark` viene applicata su <html>.
 * Il preload anti-flash è gestito da uno script inline in index.html (legge
 * localStorage PRIMA che React monti, evita lampeggio al refresh).
 */

import { useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "prora-theme";

function readInitial(): Theme {
  if (typeof window === "undefined") return "light";
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (saved === "light" || saved === "dark") return saved;
  } catch { /* localStorage non disponibile */ }
  // Fallback: prefers-color-scheme del sistema
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

function apply(theme: Theme) {
  const html = document.documentElement;
  if (theme === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => {
    apply(theme);
  }, [theme]);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  return [theme, toggle];
}
